import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useAudienceImportStatus } from "@/lib/audience-import";
import { Card, CardContent } from "@/components/ui/card";
import { ProgressBar } from "@/components/ui/progress";
import { Alert } from "@/components/ui/alert";

/**
 * Imported @usernames and phones only become audience rows after a real
 * Telegram lookup, which is deliberately slow — paced, and capped at 150/day
 * per account. Without this banner the operator would import 500 handles, see
 * nothing appear, and assume it silently failed.
 */
export function ImportProgressBanner() {
  const { t } = useTranslation();
  const { data } = useAudienceImportStatus();

  if (!data || data.status === "idle" || data.status === "done") return null;

  if (data.status === "failed") {
    return (
      <Alert tone="danger" title={t("audience_import.progress_failed")}>
        {data.error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent className="py-3">
        <div className="mb-2 flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-accent" />
          <span className="font-medium text-fg-primary">
            {t("audience_import.progress_title")}
          </span>
          {data.label && (
            <span className="truncate text-fg-muted">— {data.label}</span>
          )}
          <span className="ml-auto font-mono text-xs tabular-nums text-fg-muted">
            {data.processed}/{data.total}
          </span>
        </div>
        <ProgressBar value={data.processed} max={Math.max(data.total, 1)} compact />
        <p className="mt-2 text-xs text-fg-muted">
          {t("audience_import.progress_hint", {
            resolved: data.resolved,
            notFound: data.not_found,
          })}
        </p>
      </CardContent>
    </Card>
  );
}
